import { useState, useCallback } from 'react';
import api from '../services/api';

/**
 * Hook for availability slot CRUD operations.
 * - fetchPublicSlots(artistId, from, to) — public calendar view
 * - fetchMySlots(from, to) — dashboard private view
 * - createSlot / updateSlot / deleteSlot — single ops
 * - bulkCreate / bulkDelete — batch ops
 */
export function useAvailability() {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchPublicSlots = useCallback(async (artistId, from, to) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      const qs = params.toString();
      const data = await api.get(`/artists/${artistId}/availability${qs ? `?${qs}` : ''}`);
      setSlots(data.data.slots);
      return data.data.slots;
    } catch (err) {
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMySlots = useCallback(async (from, to, status, artistId) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      if (status) params.set('status', status);
      if (artistId) params.set('artistId', artistId);
      const qs = params.toString();
      const data = await api.get(`/availability/me${qs ? `?${qs}` : ''}`);
      setSlots(data.data.slots);
      return data.data.slots;
    } catch (err) {
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const createSlot = useCallback(async (slotData) => {
    try {
      const data = await api.post('/availability', slotData);
      setSlots((prev) => [...prev, data.data.slot]);
      return data.data.slot;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const bulkCreate = useCallback(async (slotsData) => {
    try {
      const data = await api.post('/availability/bulk', { slots: slotsData });
      setSlots((prev) => [...prev, ...data.data.slots]);
      return data.data.slots;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const updateSlot = useCallback(async (id, updates) => {
    try {
      const data = await api.put(`/availability/${id}`, updates);
      setSlots((prev) => prev.map((s) => (s.id === id ? data.data.slot : s)));
      return data.data.slot;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const deleteSlot = useCallback(async (id) => {
    try {
      await api.delete(`/availability/${id}`);
      setSlots((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const bulkDelete = useCallback(async (ids) => {
    try {
      const data = await api.delete('/availability/bulk', { ids });
      setSlots((prev) => prev.filter((s) => !ids.includes(s.id)));
      return data.data.deletedCount;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  return {
    slots,
    loading,
    error,
    fetchPublicSlots,
    fetchMySlots,
    createSlot,
    bulkCreate,
    updateSlot,
    deleteSlot,
    bulkDelete,
    setError,
  };
}
